import { ReplaySubject, timer, Subject, of, filter, fromEvent, map, race } from 'rxjs';
import { HttpEventType, HttpClient } from '@angular/common/http';
import { switchMap } from 'rxjs/operators';
import * as i0 from '@angular/core';
import { Injectable, inject, Directive } from '@angular/core';
import { DOCUMENT } from '@angular/common';

const cacheableCache = {};
/**
 * !! Do only use this util, if you generate observables e.g in functions and
 * you want to prevent multible calls of the same request. !!
 *
 * createRequest(parameters) {
 *   return this.http.get(...)
 * }
 *
 * ->
 *
 * createRequest(parameters) {
 *   return cacheable(this.http.get(...), 'myCachekey')
 * }
 */
function cacheable(returnObservable, key, options = {
    cacheFirstResult: false,
}) {
    const cacheObject = (options.customCache || cacheableCache)[key];
    // return cached observable if exists
    if (cacheObject) {
        return cacheObject;
    }
    const replay = new ReplaySubject(1);
    // See description if you are not confident with direct subscription
    returnObservable().subscribe((x) => replay.next(x), (x) => replay.error(x), () => {
        replay.complete();
        if (options.cacheFirstResult === false) {
            if (!!options.customCache) {
                delete options.customCache[key];
            }
            else {
                delete cacheableCache[key];
            }
        }
    });
    const observable = replay.asObservable();
    if (!!options.customCache) {
        options.customCache[key] = observable;
    }
    else {
        cacheableCache[key] = observable;
    }
    return observable;
}

/**
 * layout für den Operator habe ich von retryWhen() kopiert und angepasst.
 * @param http
 * @param delay
 */
function httpPollResponse(retryFunction, delay = 1000) {
    return (source) => {
        // lift verwendet weil ich die gleichen subscriber Funktionen
        // für mein inner observable verwenden möchte!
        return source.lift(function (liftedSource) {
            try {
                let complete = true;
                const subscribeForPollResponse = () => {
                    liftedSource.subscribe(
                    // hier findet die Überprüfung für das Polling statt.
                    (event) => {
                        if (event.type === HttpEventType.Response &&
                            event.status === 202 &&
                            event.headers.has('Location')) {
                            complete = false;
                            const retryAfter = event.headers.has('Retry-After')
                                ? parseInt(event.headers.get('Retry-After'), 10)
                                : NaN;
                            const pollDelayMs = isNaN(retryAfter)
                                ? delay
                                : retryAfter * 1000;
                            // Timer setzen und den http get request machen.
                            timer(pollDelayMs)
                                .pipe(switchMap(() => retryFunction(event.headers.get('Location'))))
                                .subscribe({
                                // auch hier wieder einfach von der subscription
                                // an den subscriber der äußeren Observable weiterleiten.
                                next: (innerHttpEvent) => {
                                    this.next(innerHttpEvent);
                                },
                                error: (innerErr) => this.error(innerErr),
                                complete: () => this.complete(),
                            });
                        }
                        else {
                            // Es wird vom Backend kein Polling anfragt, vom äußeren Observable
                            // den Subscriber aufrufen.
                            complete = true;
                            this.next(event);
                        }
                    }, 
                    // einfach nur durchreichen, subscriber direkt aufrufen.
                    (err) => {
                        this.error(err);
                    }, () => {
                        if (complete) {
                            this.complete();
                        }
                    });
                };
                // Start the subscription
                subscribeForPollResponse();
            }
            catch (err) {
                /* istanbul ignore next: layout copied operator from retryWhen() but could not found a way to test it.*/
                this.error(err);
            }
        });
    };
}

class HttpPollingInterceptor {
    constructor(injector) {
        this.injector = injector;
    }
    intercept(request, next) {
        const http = this.injector.get(HttpClient);
        return next
            .handle(request.clone())
            .pipe(httpPollResponse((location) => http.get(location, { observe: 'response' })));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.6", ngImport: i0, type: HttpPollingInterceptor, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.6", ngImport: i0, type: HttpPollingInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.6", ngImport: i0, type: HttpPollingInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

/**
 * Exposes Angulars' lifecycle hooks as observables.
 *
 * @example
 * export class MyComponent extends ReactiveLifecycles {
 *      constructor(){
 *        super(); // must be called before any other call in constructor
 *
 *        // will be automatically unsubscribed as
 *        // soon as the component is destroyed
 *        this.ngOnInit$.subscribe(()=>{...});
 *        // ...
 *      }
 * }
 * @usage
 * You can also use `this.ngOnDestroy$` to unsubscribe form your Observables like `this.foo$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(()=>{...})`
 *
 * The default life-cycle hooks are still available to you. Be aware that they are called after the observable counterpart emission if you call super.`lifeCycleHookName`
 */
class ReactiveLifecycles {
    constructor() {
        /**
         * Emits *once* as soon as ngOnInit hook is called.
         *
         * Unsubscribes automatically on ngOnDestroy
         */
        this.ngOnInit$ = new Subject();
        /**
         * Emits everytime ngOnChanges is called. Emits a typed SimpleChange object
         *
         * Unsubscribes automatically on ngOnDestroy
         */
        this.ngOnChanges$ = new Subject();
        /**
         * Emits everytime ngDoCheck is called.
         *
         * Unsubscribes automatically on ngOnDestroy
         */
        this.ngDoCheck$ = new Subject();
        /**
         * Emits *once* as soon as ngAfterContentInit is called.
         *
         * Unsubscribes automatically on ngOnDestroy
         */
        this.ngAfterContentInit$ = new Subject();
        /**
         * Emits everytime ngAfterContentChecked is called.
         *
         * Unsubscribes automatically on ngOnDestroy
         */
        this.ngAfterContentChecked$ = new Subject();
        /**
         * Emits *once* as soon as ngAfterViewInit is called.
         *
         * Unsubscribes automatically on ngOnDestroy
         */
        this.ngAfterViewInit$ = new Subject();
        /**
         * Emits everytime ngAfterViewChecked is called.
         *
         * Unsubscribes automatically on ngOnDestroy
         */
        this.ngAfterViewChecked$ = new Subject();
        /**
         * Emits *once* as soon as ngOnDestroy is called.
         *
         * Completes every other lifecycle observables so you don't have to
         */
        this.ngOnDestroy$ = new Subject();
        this._document = inject(DOCUMENT);
    }
    ngOnInit() {
        this.ngOnInit$.next();
        this.ngOnInit$.complete();
    }
    ngOnChanges(changes) {
        this.ngOnChanges$.next(changes);
    }
    ngDoCheck() {
        this.ngDoCheck$.next();
    }
    ngAfterContentInit() {
        this.ngAfterContentInit$.next();
        this.ngAfterContentInit$.complete();
    }
    ngAfterContentChecked() {
        this.ngAfterContentChecked$.next();
    }
    ngAfterViewInit() {
        /*
         * Patch for Safari browsers
         * Safari handles the `readystatechange` a bit different than other browsers
         * So sometimes the dom is still undergoing wild changes while Angular already triggers the `ngAfterViewInit` callback
         * This way we enforce the document to be ready before firing `ngAfterViewInit`
         */
        const documentReady$ = of(() => this._document.readyState === 'complete').pipe(filter(Boolean));
        const readyStateChangeEvents$ = fromEvent(this._document, 'readystatechange').pipe(map(() => this._document.readyState === 'complete'), filter((ready) => ready));
        race(documentReady$, readyStateChangeEvents$).subscribe(() => {
            this.ngAfterViewInit$.next();
            this.ngAfterViewInit$.complete();
        });
    }
    ngAfterViewChecked() {
        this.ngAfterViewChecked$.next();
    }
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        //cleanup
        [
            this.ngOnInit$,
            this.ngOnChanges$,
            this.ngDoCheck$,
            this.ngAfterContentInit$,
            this.ngAfterContentChecked$,
            this.ngAfterViewInit$,
            this.ngAfterViewChecked$,
        ].forEach((stream) => stream.complete());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.6", ngImport: i0, type: ReactiveLifecycles, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.6", type: ReactiveLifecycles, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.6", ngImport: i0, type: ReactiveLifecycles, decorators: [{
            type: Directive,
            args: [{}]
        }], ctorParameters: function () { return []; } });

/**
 * Generated bundle index. Do not edit.
 */

export { HttpPollingInterceptor, ReactiveLifecycles, cacheable, httpPollResponse };
